import React, { useState, useEffect, useRef } from 'react';
import { useStatusStore } from '../../stores/statusStore';
import { STATUS_NAMES, STATUS_TYPES } from '../../constants/statusConstants';
import { Link } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { FaBell, FaEnvelope, FaPhone, FaCheckCircle } from 'react-icons/fa';
import { useLeadStore } from '../../stores/leadStore';
import ClickOutside from '../UiElements/ClickOutside';

const NotificationDropdown = () => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead 
  } = useNotifications();
  
  const { 
    leads, 
    fetchLeads, 
    updateLead 
  } = useLeadStore();

  // Get status store to resolve IDs dynamically
  const { getStatusIdByNameAndType } = useStatusStore();

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch leads when component mounts
  useEffect(() => {
    const loadLeads = async () => {
      try {
        await fetchLeads();
      } catch (error) {
        console.error('Failed to fetch leads:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadLeads();
  }, [fetchLeads]);

  const handleMarkAllAsRead = async () => {
    try {
      setIsLoading(true);
      
      // Mark all notifications as read
      markAllAsRead();
      
      // Mark all unread leads as read
      const unreadLeads = leads.filter(lead => !lead.read);
      if (unreadLeads.length > 0) {
        // Get the 'Read' status ID dynamically
        const readStatusId = getStatusIdByNameAndType(STATUS_NAMES.READ, STATUS_TYPES.LEAD);
        
        if (readStatusId) {
          await Promise.all(
            unreadLeads.map(lead => 
              updateLead(lead.id, { read: true, status_id: readStatusId })
            )
          );
        } else {
          console.error('Could not find Read status ID for leads');
        }
        // Refresh the leads to update the UI
        await fetchLeads();
      }
      
      // Close the dropdown to show the changes
      setTimeout(() => {
        setIsOpen(false);
      }, 300);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      // Show error to user if needed
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (e: React.MouseEvent, id: string, isLead: boolean = false) => {
    e.stopPropagation();
    e.preventDefault();
    
    try {
      if (isLead) {
        // Get the 'Read' status ID dynamically
        const readStatusId = getStatusIdByNameAndType(STATUS_NAMES.READ, STATUS_TYPES.LEAD);
        
        if (readStatusId) {
          await updateLead(parseInt(id), { read: true, status_id: readStatusId });
          await fetchLeads();
        } else {
          console.error('Could not find Read status ID for leads');
        }
      } else {
        markAsRead(id);
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Filter and sort notifications and leads
  const filteredNotifications = notifications
    .filter(notification => !notification.read)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Get the 'Unread' status ID dynamically
  const unreadStatusId = getStatusIdByNameAndType(STATUS_NAMES.UNREAD, STATUS_TYPES.LEAD);
  
  const filteredLeads = leads
    // Only show unread leads, using dynamic ID lookup
    .filter(lead => unreadStatusId ? lead.status_id === unreadStatusId : false)
    .sort((a, b) => new Date(b.sent_date || 0).getTime() - new Date(a.sent_date || 0).getTime());

  const totalUnreadCount = unreadCount + leads.filter(lead => 
    unreadStatusId ? lead.status_id === unreadStatusId : false
  ).length;

  return (
    <ClickOutside onClick={() => setIsOpen(false)} className="relative">
      <div ref={dropdownRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="View notifications"
          className="relative flex h-10 w-10 items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
        >
          <FaBell className="h-5 w-5" />
          {totalUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-white">
              {totalUnreadCount > 9 ? '9+' : totalUnreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div className="fixed left-2 right-2 top-full mt-1 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 rounded-md bg-white shadow-lg dark:bg-boxdark z-50">
            <div className="p-3 border-b border-stroke dark:border-strokedark flex justify-between items-center">
              <h3 className="text-sm font-medium">
                Notifications {totalUnreadCount > 0 && `(${totalUnreadCount})`}
              </h3>
              {totalUnreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-primary hover:underline"
                  disabled={isLoading}
                >
                  Mark all as read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {filteredNotifications.length === 0 && filteredLeads.length === 0 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No new notifications
                </div>
              ) : (
                <>
                  {filteredNotifications.map((notification) => (
                    <div
                      key={`notification-${notification.id}`}
                      className={`px-4 py-3 hover:bg-gray-100 dark:hover:bg-boxdark-2 transition-colors flex items-start justify-between ${!notification.read ? 'bg-blue-50 dark:bg-boxdark-3' : ''}`}
                    >
                      <Link
                        to={notification.link || '#'}
                        className="flex-1"
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 pt-0.5">
                            {notification.icon || <FaBell className="h-5 w-5 text-gray-400" />}
                          </div>
                          <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {notification.title}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {notification.message}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                      </Link>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          markAsRead(notification.id);
                        }}
                        className="ml-2 p-1 text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-colors"
                        title="Mark as read"
                      >
                        <FaCheckCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ))}

                  {filteredLeads.map((lead) => (
                    <div
                      key={`lead-${lead.id}`}
                      className={`px-4 py-3 hover:bg-gray-100 dark:hover:bg-boxdark-2 transition-colors flex items-start justify-between ${!lead.read ? 'bg-blue-50 dark:bg-boxdark-3' : ''}`}
                    >
                      <Link
                        to={`/leads/${lead.id}`}
                        className="flex-1"
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 pt-0.5">
                            {lead.direction === 'incoming' ? (
                              <FaEnvelope className="h-5 w-5 text-blue-500" />
                            ) : (
                              <FaPhone className="h-5 w-5 text-green-500" />
                            )}
                          </div>
                          <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {lead.direction === 'incoming' ? 'Incoming' : 'Outgoing'} {lead.subject}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                              {lead.body || 'No message content available'}
                            </p>
                            <p className="mt-1 text-xs text-gray-400">
                              {formatDistanceToNow(new Date(lead.sent_date || lead.created_at || new Date()), { addSuffix: true })}
                            </p>
                          </div>
                        </div>
                      </Link>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          markAsRead(lead.id.toString());
                        }}
                        className="ml-2 p-1 text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-colors"
                        title="Mark as read"
                      >
                        <FaCheckCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </>
              )}
            </div>

            {(filteredNotifications.length > 0 || filteredLeads.length > 0) && (
              <div className="border-t border-stroke dark:border-strokedark p-2 text-center">
                <Link
                  to="/notifications"
                  className="text-sm font-medium text-primary hover:underline"
                  onClick={() => setIsOpen(false)}
                >
                  View all notifications
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </ClickOutside>
  );
};

export default NotificationDropdown;
